import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import './Settings.css';

// Reusable ON/OFF row (mirrors the COD toggle style). Module-level so it is not
// recreated on every render.
const ToggleRow = ({ label, on, onClick, help, disabled }) => (
  <div className="form-group">
    <label>{label}</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span style={{ fontWeight: 600, color: on ? '#15803d' : '#6b7280' }}>{on ? '🟢 ENABLED' : '🔴 DISABLED'}</span>
      <button onClick={onClick} disabled={disabled} className={on ? 'btn btn-danger' : 'btn btn-primary'}>
        {disabled ? 'Saving…' : (on ? 'Turn OFF' : 'Turn ON')}
      </button>
    </div>
    {help && <small className="form-help">{help}</small>}
  </div>
);

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSlotsUsed, setSavingSlotsUsed] = useState(false);
  const [savingDistance, setSavingDistance] = useState(false);
  const [savingTrackingMap, setSavingTrackingMap] = useState(false);
  const [savingPartnerDistance, setSavingPartnerDistance] = useState(false);
  const [savingMinPartners, setSavingMinPartners] = useState(false);
  const [savingGigOnly, setSavingGigOnly] = useState(false);
  const [savingTaxRates, setSavingTaxRates] = useState(false);
  const [newLimit, setNewLimit] = useState('');
  const [newSlotsUsed, setNewSlotsUsed] = useState('');
  const [newMaxDistance, setNewMaxDistance] = useState('');
  const [newMaxPartnerDistance, setNewMaxPartnerDistance] = useState('');
  const [newMinRequiredPartners, setNewMinRequiredPartners] = useState('');
  const [newBatchTimeout, setNewBatchTimeout] = useState('');
  const [newFirstBatchSize, setNewFirstBatchSize] = useState('');
  const [newAutoAssign, setNewAutoAssign] = useState(true);
  const [batchTimeoutMessage, setBatchTimeoutMessage] = useState({ type: '', text: '' });
  const [savingBatchTimeout, setSavingBatchTimeout] = useState(false);
  const [deliveryMarkerStyle, setDeliveryMarkerStyle] = useState('scooter');
  const [markerScale, setMarkerScale] = useState(0.75);
  // Tax Rate Settings (18% GST on platform fee and delivery fee)
  const [foodGst, setFoodGst] = useState(5);
  const [platformGst, setPlatformGst] = useState(18);
  const [deliveryGst, setDeliveryGst] = useState(18);
  const [tdsRate, setTdsRate] = useState(0.1);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [slotsUsedMessage, setSlotsUsedMessage] = useState({ type: '', text: '' });
  const [distanceMessage, setDistanceMessage] = useState({ type: '', text: '' });
  const [partnerDistanceMessage, setPartnerDistanceMessage] = useState({ type: '', text: '' });
  const [minPartnersMessage, setMinPartnersMessage] = useState({ type: '', text: '' });
  const [trackingMapMessage, setTrackingMapMessage] = useState({ type: '', text: '' });
  const [taxRatesMessage, setTaxRatesMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getSettings();
      const settingsData = response.data.data.settings;
      setSettings(settingsData);
      setNewLimit(settingsData.freeDeliveryPartnerLimit);
      setNewSlotsUsed(settingsData.freeDeliverySlotsUsed);
      setNewMaxDistance(settingsData.maxRestaurantDistance || 10);
      setNewMaxPartnerDistance(settingsData.deliveryAssignment?.maxPartnerDistance || 10);
      setNewMinRequiredPartners(settingsData.deliveryAssignment?.minRequiredPartners || 1);
      setNewBatchTimeout(settingsData.deliveryAssignment?.batchTimeoutSeconds || 600);
      setNewFirstBatchSize(settingsData.deliveryAssignment?.firstBatchSize || 7);
      setNewAutoAssign(settingsData.deliveryAssignment?.autoAssignOnFailure !== false);
      setDeliveryMarkerStyle(settingsData.trackingMapSettings?.deliveryMarkerStyle || 'scooter');
      setMarkerScale(settingsData.trackingMapSettings?.markerScale ?? 0.75);
      // Load tax rates (18% GST on platform fee and delivery fee)
      setFoodGst(settingsData.taxRates?.foodGst ?? 5);
      setPlatformGst(settingsData.taxRates?.platformGst ?? 18);
      setDeliveryGst(settingsData.taxRates?.deliveryGst ?? 18);
      setTdsRate(settingsData.taxRates?.tdsRate194O ?? 0.1);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLimit = async (e) => {
    e.preventDefault();

    if (newLimit < 0) {
      setMessage({ type: 'error', text: 'Limit must be a positive number' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await settingsAPI.updateFreeDeliveryLimit(parseInt(newLimit));
      await fetchSettings();
      setMessage({ type: 'success', text: 'Free delivery partner limit updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSlotsUsed = async (e) => {
    e.preventDefault();

    if (newSlotsUsed < 0) {
      setSlotsUsedMessage({ type: 'error', text: 'Slots used must be a positive number' });
      return;
    }

    if (newSlotsUsed > settings?.freeDeliveryPartnerLimit) {
      setSlotsUsedMessage({ type: 'error', text: `Slots used cannot exceed total limit (${settings?.freeDeliveryPartnerLimit})` });
      return;
    }

    setSavingSlotsUsed(true);
    setSlotsUsedMessage({ type: '', text: '' });

    try {
      await settingsAPI.updateSlotsUsed(parseInt(newSlotsUsed));
      await fetchSettings();
      setSlotsUsedMessage({ type: 'success', text: 'Slots used counter updated successfully!' });
    } catch (error) {
      setSlotsUsedMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update slots used' });
    } finally {
      setSavingSlotsUsed(false);
    }
  };

  const handleUpdateMaxDistance = async (e) => {
    e.preventDefault();

    const distance = parseFloat(newMaxDistance);
    if (distance < 1 || distance > 100) {
      setDistanceMessage({ type: 'error', text: 'Distance must be between 1 and 100 km' });
      return;
    }

    setSavingDistance(true);
    setDistanceMessage({ type: '', text: '' });

    try {
      await settingsAPI.updateMaxRestaurantDistance(distance);
      await fetchSettings();
      setDistanceMessage({ type: 'success', text: 'Maximum restaurant distance updated successfully!' });
    } catch (error) {
      setDistanceMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update distance' });
    } finally {
      setSavingDistance(false);
    }
  };

  const handleUpdateMaxPartnerDistance = async (e) => {
    e.preventDefault();

    const distance = parseFloat(newMaxPartnerDistance);
    if (distance < 1 || distance > 50) {
      setPartnerDistanceMessage({ type: 'error', text: 'Distance must be between 1 and 50 km' });
      return;
    }

    setSavingPartnerDistance(true);
    setPartnerDistanceMessage({ type: '', text: '' });

    try {
      await settingsAPI.updateDeliveryAssignmentSettings({ maxPartnerDistance: distance });
      await fetchSettings();
      setPartnerDistanceMessage({ type: 'success', text: 'Maximum delivery partner distance updated successfully!' });
    } catch (error) {
      setPartnerDistanceMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update distance' });
    } finally {
      setSavingPartnerDistance(false);
    }
  };

  const handleUpdateMinRequiredPartners = async (e) => {
    e.preventDefault();

    const minPartners = parseInt(newMinRequiredPartners);
    if (minPartners < 1 || minPartners > 10) {
      setMinPartnersMessage({ type: 'error', text: 'Minimum partners must be between 1 and 10' });
      return;
    }

    setSavingMinPartners(true);
    setMinPartnersMessage({ type: '', text: '' });

    try {
      await settingsAPI.updateDeliveryAssignmentSettings({ minRequiredPartners: minPartners });
      await fetchSettings();
      setMinPartnersMessage({ type: 'success', text: 'Minimum required partners updated successfully!' });
    } catch (error) {
      setMinPartnersMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update minimum partners' });
    } finally {
      setSavingMinPartners(false);
    }
  };

  const handleToggleCountOnlyGig = async () => {
    const next = !(settings?.deliveryAssignment?.countOnlyGigPartners === true);
    setSavingGigOnly(true);
    setMinPartnersMessage({ type: '', text: '' });
    try {
      await settingsAPI.updateDeliveryAssignmentSettings({ countOnlyGigPartners: next });
      await fetchSettings();
      setMinPartnersMessage({ type: 'success', text: `Availability now counts ${next ? 'ONLY gig-booked (checked-in) partners' : 'all available partners'}.` });
    } catch (error) {
      setMinPartnersMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update setting' });
    } finally {
      setSavingGigOnly(false);
    }
  };

  // COD cash-in-hand limit — global master switch + default limit
  const [savingCod, setSavingCod] = useState(false);
  const [codDefaultInput, setCodDefaultInput] = useState('');
  const [codMessage, setCodMessage] = useState({ type: '', text: '' });
  const [savingAutoRenew, setSavingAutoRenew] = useState(false);
  const [autoRenewGraceInput, setAutoRenewGraceInput] = useState('');
  const [autoRenewMessage, setAutoRenewMessage] = useState({ type: '', text: '' });

  // Telephony / IVR (Exotel) — opt-in second layer over Agora (all default OFF)
  const [telephony, setTelephony] = useState(null);
  const [telConfigured, setTelConfigured] = useState(false);
  const [savingTel, setSavingTel] = useState(false);
  const [telMessage, setTelMessage] = useState({ type: '', text: '' });
  const [ivrSupportInput, setIvrSupportInput] = useState('');
  const [ivrFlowInput, setIvrFlowInput] = useState('');
  // Order-alert-call config inputs
  const [avNumbersInput, setAvNumbersInput] = useState('');
  const [avOwnerFlowInput, setAvOwnerFlowInput] = useState('');
  const [avRestFlowInput, setAvRestFlowInput] = useState('');
  const [avRingInput, setAvRingInput] = useState('');
  const [avDelayInput, setAvDelayInput] = useState('');
  const [avTestNumber, setAvTestNumber] = useState('');

  const handleToggleCodCashLimit = async () => {
    const next = !(settings?.codCashLimit?.enabled === true);
    if (next && !window.confirm('Enable the COD cash-limit system?\n\nOnly turn this ON after the delivery-app APK that supports cash-in-hand + deposit is released. With it ON, riders collect COD up to their limit without per-order settlement, and must deposit (Cashfree) once they hit the limit.')) {
      return;
    }
    setSavingCod(true);
    setCodMessage({ type: '', text: '' });
    try {
      await settingsAPI.updateCodCashLimitSettings({ enabled: next });
      await fetchSettings();
      setCodMessage({ type: 'success', text: `COD cash-limit system ${next ? 'ENABLED' : 'DISABLED'}.` });
    } catch (error) {
      setCodMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update COD setting' });
    } finally {
      setSavingCod(false);
    }
  };

  const handleSaveCodDefaultLimit = async () => {
    const n = Number(codDefaultInput);
    if (!Number.isFinite(n) || n < 0) {
      setCodMessage({ type: 'error', text: 'Enter a valid non-negative number.' });
      return;
    }
    setSavingCod(true);
    setCodMessage({ type: '', text: '' });
    try {
      await settingsAPI.updateCodCashLimitSettings({ defaultLimit: n });
      await fetchSettings();
      setCodDefaultInput('');
      setCodMessage({ type: 'success', text: `Default COD limit set to ₹${n}.` });
    } catch (error) {
      setCodMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update default limit' });
    } finally {
      setSavingCod(false);
    }
  };

  const handleToggleAutoRenew = async () => {
    const current = settings?.subscriptionAutoRenew?.enabled !== false; // default ON
    const next = !current;
    if (!next && !window.confirm('Turn OFF proactive subscription auto-renewal?\n\nWith it OFF, a restaurant whose paid period ends will show "expired" until its owner next opens the app (the old lazy behavior).')) {
      return;
    }
    setSavingAutoRenew(true);
    setAutoRenewMessage({ type: '', text: '' });
    try {
      await settingsAPI.updateSubscriptionAutoRenewSettings({ enabled: next });
      await fetchSettings();
      setAutoRenewMessage({ type: 'success', text: `Subscription auto-renewal ${next ? 'ENABLED' : 'DISABLED'}.` });
    } catch (error) {
      setAutoRenewMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update auto-renew setting' });
    } finally {
      setSavingAutoRenew(false);
    }
  };

  const handleSaveAutoRenewGrace = async () => {
    const n = Number(autoRenewGraceInput);
    if (!Number.isFinite(n) || n < 0) {
      setAutoRenewMessage({ type: 'error', text: 'Enter a valid non-negative number of days.' });
      return;
    }
    setSavingAutoRenew(true);
    setAutoRenewMessage({ type: '', text: '' });
    try {
      await settingsAPI.updateSubscriptionAutoRenewSettings({ graceDays: n });
      await fetchSettings();
      setAutoRenewGraceInput('');
      setAutoRenewMessage({ type: 'success', text: `Grace window set to ${n} day(s).` });
    } catch (error) {
      setAutoRenewMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update grace window' });
    } finally {
      setSavingAutoRenew(false);
    }
  };

  const fetchTelephony = async () => {
    try {
      const res = await settingsAPI.getTelephonySettings();
      const d = (res.data && res.data.data) || {};
      setTelephony(d);
      setTelConfigured(d.configured === true);
    } catch (e) {
      // Non-fatal — the card simply shows OFF/defaults.
    }
  };

  useEffect(() => { fetchTelephony(); }, []);

  const patchTelephony = async (payload, successText) => {
    setSavingTel(true);
    setTelMessage({ type: '', text: '' });
    try {
      await settingsAPI.updateTelephonySettings(payload);
      await fetchTelephony();
      setTelMessage({ type: 'success', text: successText || 'Telephony settings updated.' });
    } catch (error) {
      setTelMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update telephony settings' });
    } finally {
      setSavingTel(false);
    }
  };

  // Place a one-off test alert call so the admin can hear a flow-id ring before going live.
  const testCall = async (to, flowId, which) => {
    const digits = String(to || '').replace(/\D/g, '');
    if (digits.length < 10) { setTelMessage({ type: 'error', text: 'Enter a valid test number first.' }); return; }
    if (!flowId) { setTelMessage({ type: 'error', text: `Save the ${which} flow/app id first, then test.` }); return; }
    setSavingTel(true); setTelMessage({ type: '', text: '' });
    try {
      const res = await settingsAPI.testTelephonyCall({ to, flowId });
      setTelMessage({ type: 'success', text: res.data?.message || 'Test call placed.' });
    } catch (error) {
      setTelMessage({ type: 'error', text: error.response?.data?.message || 'Test call failed.' });
    } finally {
      setSavingTel(false);
    }
  };

  const handleUpdateAssignmentSettings = async (e) => {
    e.preventDefault();

    const timeout = parseInt(newBatchTimeout);
    const batchSize = parseInt(newFirstBatchSize);
    if (timeout < 10 || timeout > 1800) {
      setBatchTimeoutMessage({ type: 'error', text: 'Timeout must be between 10 and 1800 seconds' });
      return;
    }
    if (batchSize < 1 || batchSize > 20) {
      setBatchTimeoutMessage({ type: 'error', text: 'Batch size must be between 1 and 20' });
      return;
    }

    setSavingBatchTimeout(true);
    setBatchTimeoutMessage({ type: '', text: '' });

    try {
      await settingsAPI.updateDeliveryAssignmentSettings({
        batchTimeoutSeconds: timeout,
        firstBatchSize: batchSize,
        autoAssignOnFailure: newAutoAssign
      });
      await fetchSettings();
      setBatchTimeoutMessage({ type: 'success', text: 'Assignment settings updated successfully!' });
    } catch (error) {
      setBatchTimeoutMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update' });
    } finally {
      setSavingBatchTimeout(false);
    }
  };

  const handleUpdateTrackingMapSettings = async (newStyle) => {
    setSavingTrackingMap(true);
    setTrackingMapMessage({ type: '', text: '' });

    try {
      await settingsAPI.updateTrackingMapSettings({ deliveryMarkerStyle: newStyle });
      setDeliveryMarkerStyle(newStyle);
      setTrackingMapMessage({ type: 'success', text: `Delivery marker style changed to ${newStyle === 'scooter' ? 'Scooter' : 'Luxury Car'}!` });
    } catch (error) {
      setTrackingMapMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update tracking map settings' });
    } finally {
      setSavingTrackingMap(false);
    }
  };

  const handleUpdateMarkerScale = async (newScale) => {
    setSavingTrackingMap(true);
    setTrackingMapMessage({ type: '', text: '' });

    try {
      await settingsAPI.updateTrackingMapSettings({ markerScale: newScale });
      setMarkerScale(newScale);
      setTrackingMapMessage({ type: 'success', text: `Marker size set to ${Math.round(newScale * 100)}%!` });
    } catch (error) {
      setTrackingMapMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update marker size' });
    } finally {
      setSavingTrackingMap(false);
    }
  };

  const handleUpdateTaxRates = async (e) => {
    e.preventDefault();
    setSavingTaxRates(true);
    setTaxRatesMessage({ type: '', text: '' });

    try {
      await settingsAPI.updateTaxRateSettings({
        foodGst: parseFloat(foodGst),
        platformGst: parseFloat(platformGst),
        deliveryGst: parseFloat(deliveryGst),
        tdsRate194O: parseFloat(tdsRate)
      });
      await fetchSettings();
      setTaxRatesMessage({ type: 'success', text: 'Tax rates updated successfully! Changes will apply to new orders.' });
    } catch (error) {
      setTaxRatesMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update tax rates' });
    } finally {
      setSavingTaxRates(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <Navbar />
        <div className="admin-content">
          <div className="loading">Loading settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <Navbar />
      <div className="admin-content">
        <div className="admin-header">
          <h1>System Settings</h1>
          <p>Manage application-wide settings and configurations</p>
        </div>

        <div className="settings-container">
          {/* Free Delivery Partner Settings */}
          <div className="settings-card">
            <h2>Free Delivery Partner Onboarding</h2>

            {/* Current Status Display */}
            <div className="settings-status">
              <div className="status-grid">
                <div className="status-item">
                  <div className="status-label">Total Free Slots</div>
                  <div className="status-value">{settings?.freeDeliveryPartnerLimit || 0}</div>
                </div>
                <div className="status-item">
                  <div className="status-label">Slots Used</div>
                  <div className="status-value">{settings?.freeDeliverySlotsUsed || 0}</div>
                </div>
                <div className="status-item">
                  <div className="status-label">Remaining Slots</div>
                  <div className="status-value highlight">
                    {(settings?.freeDeliveryPartnerLimit || 0) - (settings?.freeDeliverySlotsUsed || 0)}
                  </div>
                </div>
              </div>

              <div className="progress-bar-container">
                <div
                  className="progress-bar"
                  style={{
                    width: `${settings ? (settings.freeDeliverySlotsUsed / settings.freeDeliveryPartnerLimit) * 100 : 0}%`
                  }}
                ></div>
              </div>
              <p className="progress-text">
                {settings?.freeDeliverySlotsUsed || 0} out of {settings?.freeDeliveryPartnerLimit || 0} free slots used
              </p>
            </div>

            {/* Update Limit Form */}
            <div className="settings-form">
              <h3>Update Free Delivery Partner Limit</h3>
              <p className="form-description">
                Set the maximum number of delivery partners who can complete onboarding for free.
                This applies to new registrations only.
              </p>

              <form onSubmit={handleUpdateLimit}>
                <div className="form-group">
                  <label htmlFor="newLimit">Free Delivery Partner Limit</label>
                  <input
                    type="number"
                    id="newLimit"
                    value={newLimit}
                    onChange={(e) => setNewLimit(e.target.value)}
                    min="0"
                    required
                    className="form-control"
                  />
                  <small className="form-help">
                    Current: {settings?.freeDeliveryPartnerLimit || 0} |
                    Used: {settings?.freeDeliverySlotsUsed || 0}
                  </small>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving || newLimit === settings?.freeDeliveryPartnerLimit.toString()}
                >
                  {saving ? 'Updating...' : 'Update Limit'}
                </button>
              </form>

              {message.text && (
                <div className={`message ${message.type}`}>
                  {message.text}
                </div>
              )}
            </div>

            {/* Update Slots Used Form */}
            <div className="settings-form" style={{ borderTop: '2px solid #e0e0e0', marginTop: '30px', paddingTop: '30px' }}>
              <h3>Adjust Slots Used Counter</h3>
              <p className="form-description">
                Manually adjust the number of free slots that have been used. This is useful for corrections or adjustments.
              </p>

              <form onSubmit={handleUpdateSlotsUsed}>
                <div className="form-group">
                  <label htmlFor="newSlotsUsed">Slots Used</label>
                  <input
                    type="number"
                    id="newSlotsUsed"
                    value={newSlotsUsed}
                    onChange={(e) => setNewSlotsUsed(e.target.value)}
                    min="0"
                    max={settings?.freeDeliveryPartnerLimit || 0}
                    required
                    className="form-control"
                  />
                  <small className="form-help">
                    Current: {settings?.freeDeliverySlotsUsed || 0} |
                    Maximum: {settings?.freeDeliveryPartnerLimit || 0} |
                    Remaining: {(settings?.freeDeliveryPartnerLimit || 0) - (settings?.freeDeliverySlotsUsed || 0)}
                  </small>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingSlotsUsed || newSlotsUsed === settings?.freeDeliverySlotsUsed.toString()}
                >
                  {savingSlotsUsed ? 'Updating...' : 'Update Slots Used'}
                </button>
              </form>

              {slotsUsedMessage.text && (
                <div className={`message ${slotsUsedMessage.type}`}>
                  {slotsUsedMessage.text}
                </div>
              )}
            </div>
          </div>

          {/* Restaurant Distance Settings */}
          <div className="settings-card">
            <h2>Restaurant Distance Filter</h2>

            <div className="settings-status">
              <div className="status-grid">
                <div className="status-item">
                  <div className="status-label">Current Max Distance</div>
                  <div className="status-value">{settings?.maxRestaurantDistance || 10} km</div>
                </div>
              </div>
              <p className="status-description">
                Users will only see restaurants within this distance from their location.
              </p>
            </div>

            <div className="settings-form">
              <h3>Update Maximum Restaurant Distance</h3>
              <p className="form-description">
                Set the maximum distance (in kilometers) within which restaurants will be shown to users.
                This helps ensure users see only nearby restaurants that can deliver to them.
              </p>

              <form onSubmit={handleUpdateMaxDistance}>
                <div className="form-group">
                  <label htmlFor="maxDistance">Maximum Distance (km)</label>
                  <input
                    type="number"
                    id="maxDistance"
                    value={newMaxDistance}
                    onChange={(e) => setNewMaxDistance(e.target.value)}
                    min="1"
                    max="100"
                    step="0.5"
                    required
                    className="form-control"
                  />
                  <small className="form-help">
                    Current: {settings?.maxRestaurantDistance || 10} km | Range: 1 - 100 km
                  </small>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingDistance || newMaxDistance === (settings?.maxRestaurantDistance || 10).toString()}
                >
                  {savingDistance ? 'Updating...' : 'Update Distance'}
                </button>
              </form>

              {distanceMessage.text && (
                <div className={`message ${distanceMessage.type}`}>
                  {distanceMessage.text}
                </div>
              )}
            </div>
          </div>

          {/* Delivery Partner Distance Settings */}
          <div className="settings-card">
            <h2>Delivery Partner Distance Filter</h2>

            <div className="settings-status">
              <div className="status-grid">
                <div className="status-item">
                  <div className="status-label">Current Max Distance</div>
                  <div className="status-value">{settings?.deliveryAssignment?.maxPartnerDistance || 10} km</div>
                </div>
              </div>
              <p className="status-description">
                Delivery partners beyond this distance from the restaurant will not receive order requests.
              </p>
            </div>

            <div className="settings-form">
              <h3>Update Maximum Delivery Partner Distance</h3>
              <p className="form-description">
                Set the maximum distance (in kilometers) from the restaurant within which delivery partners can receive order requests.
                This helps ensure quick deliveries by only notifying nearby partners.
              </p>

              <form onSubmit={handleUpdateMaxPartnerDistance}>
                <div className="form-group">
                  <label htmlFor="maxPartnerDistance">Maximum Distance (km)</label>
                  <input
                    type="number"
                    id="maxPartnerDistance"
                    value={newMaxPartnerDistance}
                    onChange={(e) => setNewMaxPartnerDistance(e.target.value)}
                    min="1"
                    max="50"
                    step="0.5"
                    required
                    className="form-control"
                  />
                  <small className="form-help">
                    Current: {settings?.deliveryAssignment?.maxPartnerDistance || 10} km | Range: 1 - 50 km
                  </small>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingPartnerDistance || newMaxPartnerDistance === (settings?.deliveryAssignment?.maxPartnerDistance || 10).toString()}
                >
                  {savingPartnerDistance ? 'Updating...' : 'Update Distance'}
                </button>
              </form>

              {partnerDistanceMessage.text && (
                <div className={`message ${partnerDistanceMessage.type}`}>
                  {partnerDistanceMessage.text}
                </div>
              )}
            </div>

            {/* Minimum Required Partners Section */}
            <div className="settings-form" style={{ borderTop: '2px solid #e0e0e0', marginTop: '30px', paddingTop: '30px' }}>
              <h3>Minimum Required Delivery Partners</h3>
              <p className="form-description">
                Set the minimum number of available delivery partners required for delivery to be shown as available to customers.
                If fewer partners are available near the restaurant, delivery option will be disabled.
              </p>

              <div className="settings-status" style={{ marginBottom: '20px' }}>
                <div className="status-grid">
                  <div className="status-item">
                    <div className="status-label">Current Minimum</div>
                    <div className="status-value">{settings?.deliveryAssignment?.minRequiredPartners || 1} partner(s)</div>
                  </div>
                </div>
              </div>

              <form onSubmit={handleUpdateMinRequiredPartners}>
                <div className="form-group">
                  <label htmlFor="minRequiredPartners">Minimum Partners Required</label>
                  <input
                    type="number"
                    id="minRequiredPartners"
                    value={newMinRequiredPartners}
                    onChange={(e) => setNewMinRequiredPartners(e.target.value)}
                    min="1"
                    max="10"
                    required
                    className="form-control"
                  />
                  <small className="form-help">
                    Current: {settings?.deliveryAssignment?.minRequiredPartners || 1} | Range: 1 - 10 partners
                  </small>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingMinPartners || newMinRequiredPartners === (settings?.deliveryAssignment?.minRequiredPartners || 1).toString()}
                >
                  {savingMinPartners ? 'Updating...' : 'Update Minimum Partners'}
                </button>
              </form>

              {minPartnersMessage.text && (
                <div className={`message ${minPartnersMessage.type}`}>
                  {minPartnersMessage.text}
                </div>
              )}

              {/* Toggle: count only gig-booked (checked-in) partners for availability */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
                <label>Count only gig-booked partners for availability</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px' }}>
                  <span style={{
                    fontWeight: 700,
                    color: settings?.deliveryAssignment?.countOnlyGigPartners === true ? '#15803d' : '#6b7280'
                  }}>
                    {settings?.deliveryAssignment?.countOnlyGigPartners === true ? 'ON — gig-booked only' : 'OFF — all available partners'}
                  </span>
                  <button
                    type="button"
                    className={settings?.deliveryAssignment?.countOnlyGigPartners === true ? 'btn btn-danger' : 'btn btn-primary'}
                    onClick={handleToggleCountOnlyGig}
                    disabled={savingGigOnly}
                  >
                    {savingGigOnly ? 'Saving…' : (settings?.deliveryAssignment?.countOnlyGigPartners === true ? 'Turn OFF' : 'Turn ON')}
                  </button>
                </div>
                <small className="form-help">
                  When ON, the "delivery available near restaurant" check (shown to customers) counts only partners currently checked into a gig slot — not all online partners.
                </small>
              </div>
            </div>
          </div>

          {/* COD Cash-in-Hand Limit */}
          <div className="settings-card">
            <h2>💵 COD Cash-in-Hand Limit</h2>
            <p style={{ color: 'var(--gray-500)', fontSize: '13px', marginBottom: '16px' }}>
              Lets delivery partners hold collected COD cash up to a limit before they must deposit it
              online (Cashfree). Below the limit, no per-order settlement is required; once a partner hits
              their limit they're blocked from new COD orders until they deposit. Prepaid orders are never affected.
            </p>
            {codMessage.text && (
              <div className={`alert ${codMessage.type === 'error' ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: '12px' }}>
                {codMessage.text}
              </div>
            )}
            <div className="form-group">
              <label>Status</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontWeight: 600, color: settings?.codCashLimit?.enabled === true ? '#15803d' : '#6b7280' }}>
                  {settings?.codCashLimit?.enabled === true ? '🟢 ENABLED' : '🔴 DISABLED'}
                </span>
                <button
                  onClick={handleToggleCodCashLimit}
                  disabled={savingCod}
                  className={settings?.codCashLimit?.enabled === true ? 'btn btn-danger' : 'btn btn-primary'}
                >
                  {savingCod ? 'Saving…' : (settings?.codCashLimit?.enabled === true ? 'Turn OFF' : 'Turn ON')}
                </button>
              </div>
              <small className="form-help" style={{ color: '#b45309' }}>
                ⚠️ Turn ON only after the delivery-app APK with cash-in-hand + deposit support is released,
                otherwise partners on the old app could hit the limit with no in-app way to deposit.
              </small>
            </div>
            <div className="form-group">
              <label>Default per-partner limit (₹)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="number"
                  min="0"
                  placeholder={String(settings?.codCashLimit?.defaultLimit ?? 1000)}
                  value={codDefaultInput}
                  onChange={(e) => setCodDefaultInput(e.target.value)}
                  style={{ maxWidth: '160px' }}
                />
                <button onClick={handleSaveCodDefaultLimit} disabled={savingCod} className="btn btn-primary">
                  {savingCod ? 'Saving…' : 'Save'}
                </button>
              </div>
              <small className="form-help">
                Current default: <strong>₹{settings?.codCashLimit?.defaultLimit ?? 1000}</strong>. Per-partner overrides are set on the Delivery Partners page.
              </small>
            </div>
          </div>

          {/* Proactive subscription auto-renewal */}
          <div className="settings-card">
            <h2>🔄 Subscription Auto-Renewal</h2>
            <p style={{ color: 'var(--gray-500)', fontSize: '13px', marginBottom: '16px' }}>
              Renews restaurant subscriptions — and converts trial-ended restaurants to paid — on schedule the
              moment a period ends, instead of only when the owner next opens the app, so there's no "expired" gap.
              Uses the same charge + per-period idempotency as the normal flow (never double-charges), skips
              cancelled/blocked and brand-new never-used subscriptions, and only acts on subscriptions that came due
              within the grace window below (so long-abandoned restaurants aren't billed).
            </p>
            {autoRenewMessage.text && (
              <div className={`alert ${autoRenewMessage.type === 'error' ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: '12px' }}>
                {autoRenewMessage.text}
              </div>
            )}
            <div className="form-group">
              <label>Status</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontWeight: 600, color: settings?.subscriptionAutoRenew?.enabled !== false ? '#15803d' : '#6b7280' }}>
                  {settings?.subscriptionAutoRenew?.enabled !== false ? '🟢 ENABLED' : '🔴 DISABLED'}
                </span>
                <button
                  onClick={handleToggleAutoRenew}
                  disabled={savingAutoRenew}
                  className={settings?.subscriptionAutoRenew?.enabled !== false ? 'btn btn-danger' : 'btn btn-primary'}
                >
                  {savingAutoRenew ? 'Saving…' : (settings?.subscriptionAutoRenew?.enabled !== false ? 'Turn OFF' : 'Turn ON')}
                </button>
              </div>
              <small className="form-help">
                When OFF, subscriptions fall back to the old lazy behavior (renew only on next app-open).
              </small>
            </div>
            <div className="form-group">
              <label>Grace window (days)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="number"
                  min="0"
                  placeholder={String(settings?.subscriptionAutoRenew?.graceDays ?? 3)}
                  value={autoRenewGraceInput}
                  onChange={(e) => setAutoRenewGraceInput(e.target.value)}
                  style={{ maxWidth: '160px' }}
                />
                <button onClick={handleSaveAutoRenewGrace} disabled={savingAutoRenew} className="btn btn-primary">
                  {savingAutoRenew ? 'Saving…' : 'Save'}
                </button>
              </div>
              <small className="form-help">
                Current: <strong>{settings?.subscriptionAutoRenew?.graceDays ?? 3} day(s)</strong>. Only subscriptions that came due within this many days are auto-renewed; older ones renew on next app-open.
              </small>
            </div>
          </div>

          {/* Telephony / IVR (Exotel) — opt-in second layer over Agora */}
          <div className="settings-card">
            <h2>📞 Telephony / IVR (Exotel)</h2>
            <p style={{ color: 'var(--gray-500)', fontSize: '13px', marginBottom: '16px' }}>
              A SECOND, opt-in calling option on top of the current in-app (Agora) calling — number masking for
              order calls, an IVR support line, and automated voice. Everything below defaults OFF; the current
              calling is unaffected until you enable a feature AND Exotel credentials + a virtual number are set
              on the server.
            </p>
            {telMessage.text && (
              <div className={`alert ${telMessage.type === 'error' ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: '12px' }}>
                {telMessage.text}
              </div>
            )}
            <div className="form-group">
              <small className="form-help" style={{ color: telConfigured ? '#15803d' : '#b45309' }}>
                {telConfigured
                  ? '✅ Exotel credentials detected on the server.'
                  : '⚠️ Exotel credentials not set on the server yet — features stay inactive (safe) until credentials + a virtual number are configured.'}
              </small>
            </div>

            <ToggleRow
              label="Master switch"
              on={telephony?.enabled === true}
              disabled={savingTel}
              help="Turns the whole telephony layer on/off. Each feature below also has its own switch."
              onClick={() => {
                const on = telephony?.enabled === true;
                if (!on && !window.confirm('Enable the Exotel telephony layer?\n\nThis is a SECOND option on top of the current in-app (Agora) calling. Nothing changes for users until you also turn on a specific feature below AND server credentials + a virtual number are set.')) return;
                patchTelephony({ enabled: !on }, `Telephony ${!on ? 'ENABLED' : 'DISABLED'}.`);
              }}
            />

            {telephony?.enabled === true && (
              <>
                <hr style={{ margin: '18px 0', border: 0, borderTop: '1px solid #eee' }} />
                <div style={{ padding: '2px 0 10px' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Provider</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[
                      { key: 'exotel', label: 'Exotel' },
                      { key: 'msg91', label: 'MSG91' },
                    ].map(opt => {
                      const active = (telephony?.provider || 'exotel') === opt.key;
                      const cfg = telephony?.providersConfigured?.[opt.key] === true;
                      return (
                        <button key={opt.key} type="button" disabled={savingTel}
                          onClick={() => patchTelephony({ provider: opt.key }, `Telephony provider set to ${opt.label}.`)}
                          style={{
                            padding: '6px 12px', borderRadius: 6, cursor: savingTel ? 'default' : 'pointer',
                            border: active ? '2px solid #2e7d32' : '1px solid #ccc',
                            background: active ? '#e8f5e9' : '#fff',
                            fontWeight: active ? 700 : 400, fontSize: 13,
                          }}>
                          {opt.label} {cfg ? '✓' : '⚠️'}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    ✓ = credentials configured in server env; ⚠️ = not configured (masking will no-op until creds are set). Switching provider takes effect immediately for new calls.
                  </div>
                </div>
                <h3 style={{ margin: '0 0 8px' }}>Call masking</h3>
                <ToggleRow
                  label="Call masking"
                  on={telephony?.callMasking?.enabled === true}
                  disabled={savingTel}
                  help="Connect two parties through a virtual number so real phone numbers stay private."
                  onClick={() => patchTelephony({ callMasking: { enabled: !(telephony?.callMasking?.enabled === true) } })}
                />
                {telephony?.callMasking?.enabled === true && (
                  <>
                    <ToggleRow label="— Customer ↔ Delivery" on={telephony?.callMasking?.customerDelivery === true} disabled={savingTel}
                      onClick={() => patchTelephony({ callMasking: { customerDelivery: !(telephony?.callMasking?.customerDelivery === true) } })} />
                    <ToggleRow label="— Customer ↔ Restaurant" on={telephony?.callMasking?.customerRestaurant === true} disabled={savingTel}
                      onClick={() => patchTelephony({ callMasking: { customerRestaurant: !(telephony?.callMasking?.customerRestaurant === true) } })} />
                    <ToggleRow label="— Delivery ↔ Restaurant" on={telephony?.callMasking?.deliveryRestaurant === true} disabled={savingTel}
                      onClick={() => patchTelephony({ callMasking: { deliveryRestaurant: !(telephony?.callMasking?.deliveryRestaurant === true) } })} />
                    <ToggleRow label="— Record masked calls" on={telephony?.callMasking?.record === true} disabled={savingTel}
                      help="Requires a consent disclaimer in the flow. Recordings are stored by Exotel."
                      onClick={() => patchTelephony({ callMasking: { record: !(telephony?.callMasking?.record === true) } })} />
                    <div style={{ padding: '10px 0 4px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>— Connection mode</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[
                          { key: 'bridge', label: 'Bridge (we call both)' },
                          { key: 'dial', label: 'Dial (customer dials number)' },
                        ].map(opt => {
                          const active = (telephony?.callMasking?.mode || 'bridge') === opt.key;
                          return (
                            <button key={opt.key} type="button" disabled={savingTel}
                              onClick={() => patchTelephony({ callMasking: { mode: opt.key } })}
                              style={{
                                padding: '6px 12px', borderRadius: 6, cursor: savingTel ? 'default' : 'pointer',
                                border: active ? '2px solid #2e7d32' : '1px solid #ccc',
                                background: active ? '#e8f5e9' : '#fff',
                                fontWeight: active ? 700 : 400, fontSize: 13,
                              }}>
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                        Dial mode needs the Exotel Connect-applet Flow set up on the ExoPhone first. Keep on Bridge until then.
                      </div>
                    </div>
                  </>
                )}

                <hr style={{ margin: '18px 0', border: 0, borderTop: '1px solid #eee' }} />
                <h3 style={{ margin: '0 0 8px' }}>IVR support line</h3>
                <ToggleRow
                  label="IVR"
                  on={telephony?.ivr?.enabled === true}
                  disabled={savingTel}
                  help="A phone-menu support line (the menu itself is built in the Exotel dashboard flow)."
                  onClick={() => patchTelephony({ ivr: { enabled: !(telephony?.ivr?.enabled === true) } })}
                />
                {telephony?.ivr?.enabled === true && (
                  <>
                    <div className="form-group">
                      <label>Support / agent number the IVR routes to</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="text" placeholder={telephony?.ivr?.supportNumber || '+91…'} value={ivrSupportInput}
                          onChange={(e) => setIvrSupportInput(e.target.value)} style={{ maxWidth: '220px' }} />
                        <button className="btn btn-primary" disabled={savingTel}
                          onClick={() => { patchTelephony({ ivr: { supportNumber: ivrSupportInput } }, 'Support number saved.'); setIvrSupportInput(''); }}>Save</button>
                      </div>
                      <small className="form-help">Current: <strong>{telephony?.ivr?.supportNumber || '—'}</strong></small>
                    </div>
                    <div className="form-group">
                      <label>Exotel flow / app id</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="text" placeholder={telephony?.ivr?.flowId || 'flow id'} value={ivrFlowInput}
                          onChange={(e) => setIvrFlowInput(e.target.value)} style={{ maxWidth: '220px' }} />
                        <button className="btn btn-primary" disabled={savingTel}
                          onClick={() => { patchTelephony({ ivr: { flowId: ivrFlowInput } }, 'Flow id saved.'); setIvrFlowInput(''); }}>Save</button>
                      </div>
                      <small className="form-help">Current: <strong>{telephony?.ivr?.flowId || '—'}</strong></small>
                    </div>
                  </>
                )}

                <hr style={{ margin: '18px 0', border: 0, borderTop: '1px solid #eee' }} />
                <h3 style={{ margin: '0 0 8px' }}>Automated voice</h3>
                <ToggleRow
                  label="Automated voice"
                  on={telephony?.automatedVoice?.enabled === true}
                  disabled={savingTel}
                  help="Automated outbound calls (OTP-by-call fallback, order alerts)."
                  onClick={() => patchTelephony({ automatedVoice: { enabled: !(telephony?.automatedVoice?.enabled === true) } })}
                />
                {telephony?.automatedVoice?.enabled === true && (
                  <>
                    <ToggleRow label="— OTP by call (fallback)" on={telephony?.automatedVoice?.otpByCall === true} disabled={savingTel}
                      onClick={() => patchTelephony({ automatedVoice: { otpByCall: !(telephony?.automatedVoice?.otpByCall === true) } })} />
                    <ToggleRow label="— Order alert calls" on={telephony?.automatedVoice?.orderAlerts === true} disabled={savingTel}
                      help="On a new order, rings the owner numbers in priority order until one answers. If an order is still PENDING after the delay, calls the restaurant. ⚠ each call costs money."
                      onClick={() => patchTelephony({ automatedVoice: { orderAlerts: !(telephony?.automatedVoice?.orderAlerts === true) } })} />
                    {telephony?.automatedVoice?.orderAlerts === true && (
                      <div style={{ margin: '8px 0 4px 16px', paddingLeft: 12, borderLeft: '2px solid #eee' }}>
                        <small className="form-help" style={{ display: 'block', marginBottom: 10 }}>
                          Each message is an Exotel voice <b>App/flow</b> you build in the Exotel dashboard — paste its flow/app id (or full App URL) below.
                        </small>
                        <div className="form-group">
                          <label>Owner alert numbers <small>(priority order, comma-separated)</small></label>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input type="text" placeholder="9219559111, 9456800200, 7454883348" value={avNumbersInput} onChange={e => setAvNumbersInput(e.target.value)} style={{ maxWidth: 340 }} />
                            <button className="btn btn-primary" disabled={savingTel} onClick={() => { patchTelephony({ automatedVoice: { ownerAlertNumbers: avNumbersInput.split(',').map(s => s.trim()).filter(Boolean) } }, 'Owner numbers saved.'); setAvNumbersInput(''); }}>Save</button>
                          </div>
                          <small className="form-help">Current: <strong>{(telephony?.automatedVoice?.ownerAlertNumbers || []).join(', ') || '—'}</strong></small>
                        </div>
                        <div className="form-group">
                          <label>Owner message — Exotel flow/app id <small>(plays "New order Foodwinc")</small></label>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input type="text" placeholder={telephony?.automatedVoice?.ownerAlertFlowId || 'flow id or App URL'} value={avOwnerFlowInput} onChange={e => setAvOwnerFlowInput(e.target.value)} style={{ maxWidth: 340 }} />
                            <button className="btn btn-primary" disabled={savingTel} onClick={() => { patchTelephony({ automatedVoice: { ownerAlertFlowId: avOwnerFlowInput } }, 'Owner flow id saved.'); setAvOwnerFlowInput(''); }}>Save</button>
                          </div>
                          <small className="form-help">Current: <strong>{telephony?.automatedVoice?.ownerAlertFlowId || '—'}</strong></small>
                        </div>
                        <div className="form-group">
                          <label>Restaurant message — Exotel flow/app id <small>(plays "FoodWinc order please check your app")</small></label>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input type="text" placeholder={telephony?.automatedVoice?.restaurantAlertFlowId || 'flow id or App URL'} value={avRestFlowInput} onChange={e => setAvRestFlowInput(e.target.value)} style={{ maxWidth: 340 }} />
                            <button className="btn btn-primary" disabled={savingTel} onClick={() => { patchTelephony({ automatedVoice: { restaurantAlertFlowId: avRestFlowInput } }, 'Restaurant flow id saved.'); setAvRestFlowInput(''); }}>Save</button>
                          </div>
                          <small className="form-help">Current: <strong>{telephony?.automatedVoice?.restaurantAlertFlowId || '—'}</strong></small>
                        </div>
                        <div className="form-group">
                          <label>Ring timeout (sec) & restaurant nudge delay (min)</label>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input type="number" min="10" max="60" placeholder={`ring ${telephony?.automatedVoice?.ringTimeoutSec ?? 25}s`} value={avRingInput} onChange={e => setAvRingInput(e.target.value)} style={{ width: 130 }} />
                            <input type="number" min="1" max="30" placeholder={`delay ${telephony?.automatedVoice?.restaurantAlertDelayMin ?? 3}m`} value={avDelayInput} onChange={e => setAvDelayInput(e.target.value)} style={{ width: 130 }} />
                            <button className="btn btn-primary" disabled={savingTel} onClick={() => { const p = {}; if (avRingInput !== '') p.ringTimeoutSec = Number(avRingInput); if (avDelayInput !== '') p.restaurantAlertDelayMin = Number(avDelayInput); if (Object.keys(p).length) { patchTelephony({ automatedVoice: p }, 'Timing saved.'); setAvRingInput(''); setAvDelayInput(''); } }}>Save</button>
                          </div>
                          <small className="form-help">Ring each number <strong>{telephony?.automatedVoice?.ringTimeoutSec ?? 25}s</strong> · nudge restaurant after <strong>{telephony?.automatedVoice?.restaurantAlertDelayMin ?? 3} min</strong></small>
                        </div>
                        <ToggleRow label="— Also nudge on online (UPI/card) orders" on={telephony?.automatedVoice?.restaurantAlertOnline !== false} disabled={savingTel}
                          onClick={() => patchTelephony({ automatedVoice: { restaurantAlertOnline: !(telephony?.automatedVoice?.restaurantAlertOnline !== false) } })} />
                        <div className="form-group" style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed #ddd' }}>
                          <label>🔔 Test a call <small>(rings the number below with the SAVED message so you can hear it — works even before you toggle the service on)</small></label>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input type="text" placeholder="your number, e.g. 9219559111" value={avTestNumber} onChange={e => setAvTestNumber(e.target.value)} style={{ maxWidth: 220 }} />
                            <button className="btn btn-secondary" disabled={savingTel} onClick={() => testCall(avTestNumber, telephony?.automatedVoice?.ownerAlertFlowId, 'owner')}>📞 Test owner message</button>
                            <button className="btn btn-secondary" disabled={savingTel} onClick={() => testCall(avTestNumber, telephony?.automatedVoice?.restaurantAlertFlowId, 'restaurant')}>📞 Test restaurant message</button>
                          </div>
                          <small className="form-help">Each button rings your number and plays that message's saved Exotel flow. Save the flow-id above first.</small>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Delivery Partner Subsidy (Platform Bonus) */}
          <div className="settings-card">
            <h2>Delivery Partner Subsidy (Platform Bonus)</h2>
            <p style={{ color: 'var(--gray-500)', fontSize: '13px', marginBottom: '16px' }}>
              Extra per-delivery bonus funded by the platform. Not charged to customer. Increases rider earnings to attract delivery partners.
            </p>
            <div className="settings-info">
              <div className="status-row">
                <span className="status-label">Status</span>
                <div className="status-value">{settings?.deliveryPartnerSubsidy?.enabled ? '✅ Active' : '❌ Disabled'}</div>
              </div>
              {settings?.deliveryPartnerSubsidy?.enabled && (
                <>
                  <div className="status-row">
                    <span className="status-label">Type</span>
                    <div className="status-value">{settings.deliveryPartnerSubsidy.type || 'fixed'}</div>
                  </div>
                  {(settings.deliveryPartnerSubsidy.type === 'fixed' || settings.deliveryPartnerSubsidy.type === 'both') && (
                    <div className="status-row">
                      <span className="status-label">Fixed Amount</span>
                      <div className="status-value">₹{settings.deliveryPartnerSubsidy.fixedAmount || 0} per delivery</div>
                    </div>
                  )}
                  {(settings.deliveryPartnerSubsidy.type === 'percentage' || settings.deliveryPartnerSubsidy.type === 'both') && (
                    <div className="status-row">
                      <span className="status-label">Percentage</span>
                      <div className="status-value">{settings.deliveryPartnerSubsidy.percentage || 0}% of delivery fee</div>
                    </div>
                  )}
                  {settings.deliveryPartnerSubsidy.maxSubsidy > 0 && (
                    <div className="status-row">
                      <span className="status-label">Max Cap</span>
                      <div className="status-value">₹{settings.deliveryPartnerSubsidy.maxSubsidy} per delivery</div>
                    </div>
                  )}
                </>
              )}
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              const form = e.target;
              try {
                await settingsAPI.updateDeliveryPartnerSubsidy({
                  enabled: form.subsidyEnabled.checked,
                  type: form.subsidyType.value,
                  fixedAmount: parseFloat(form.subsidyFixed.value) || 0,
                  percentage: parseFloat(form.subsidyPercent.value) || 0,
                  maxSubsidy: parseFloat(form.subsidyMax.value) || 0
                });
                await fetchSettings();
                alert('Subsidy settings updated!');
              } catch (err) {
                alert(err.response?.data?.message || 'Failed to update');
              }
            }}>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" name="subsidyEnabled" defaultChecked={settings?.deliveryPartnerSubsidy?.enabled || false} style={{ width: 'auto' }} />
                  Enable delivery partner subsidy
                </label>
              </div>
              <div className="form-group">
                <label>Subsidy Type</label>
                <select name="subsidyType" defaultValue={settings?.deliveryPartnerSubsidy?.type || 'fixed'}>
                  <option value="fixed">Fixed Amount (₹ per delivery)</option>
                  <option value="percentage">Percentage of Delivery Fee</option>
                  <option value="both">Both (Fixed + Percentage)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Fixed Amount (₹ per delivery)</label>
                <input type="number" name="subsidyFixed" step="0.5" min="0" defaultValue={settings?.deliveryPartnerSubsidy?.fixedAmount || 0} />
                <small>e.g., ₹20 extra per delivery</small>
              </div>
              <div className="form-group">
                <label>Percentage of Delivery Fee (%)</label>
                <input type="number" name="subsidyPercent" step="1" min="0" max="100" defaultValue={settings?.deliveryPartnerSubsidy?.percentage || 0} />
                <small>e.g., 50% means if delivery fee is ₹40, subsidy is ₹20</small>
              </div>
              <div className="form-group">
                <label>Max Subsidy Cap (₹ per delivery)</label>
                <input type="number" name="subsidyMax" step="1" min="0" defaultValue={settings?.deliveryPartnerSubsidy?.maxSubsidy || 0} />
                <small>0 = no cap. Limits how much subsidy a single delivery can get.</small>
              </div>
              <button type="submit" className="btn-primary">Update Subsidy Settings</button>
            </form>
          </div>

          {/* Delivery Assignment Algorithm Settings (7+7+R) */}
          <div className="settings-card">
            <h2>Delivery Assignment Algorithm (7+7+R)</h2>
            <div className="settings-info">
              <div className="status-row">
                <span className="status-label">Batch Size</span>
                <div className="status-value">{settings?.deliveryAssignment?.firstBatchSize || 7} partners per batch</div>
              </div>
              <div className="status-row">
                <span className="status-label">Batch Timeout</span>
                <div className="status-value">{settings?.deliveryAssignment?.batchTimeoutSeconds || 600}s ({Math.round((settings?.deliveryAssignment?.batchTimeoutSeconds || 600) / 60)} min)</div>
              </div>
              <div className="status-row">
                <span className="status-label">Auto-Assign on Failure</span>
                <div className="status-value">{settings?.deliveryAssignment?.autoAssignOnFailure !== false ? 'Enabled' : 'Disabled'}</div>
              </div>
            </div>

            <form onSubmit={handleUpdateAssignmentSettings}>
              <div className="form-group">
                <label htmlFor="firstBatchSize">Partners Per Batch</label>
                <input
                  type="number"
                  id="firstBatchSize"
                  value={newFirstBatchSize}
                  onChange={(e) => setNewFirstBatchSize(e.target.value)}
                  min="1"
                  max="20"
                />
                <small>How many delivery partners to notify in each batch (1-20)</small>
              </div>
              <div className="form-group">
                <label htmlFor="batchTimeout">Batch Timeout (seconds)</label>
                <input
                  type="number"
                  id="batchTimeout"
                  value={newBatchTimeout}
                  onChange={(e) => setNewBatchTimeout(e.target.value)}
                  min="10"
                  max="1800"
                />
                <small>
                  Time to wait before sending next batch.
                  Current: {settings?.deliveryAssignment?.batchTimeoutSeconds || 600}s ({Math.round((settings?.deliveryAssignment?.batchTimeoutSeconds || 600) / 60)} min).
                  Range: 10s - 1800s (30 min)
                </small>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newAutoAssign}
                    onChange={(e) => setNewAutoAssign(e.target.checked)}
                    style={{ width: 'auto' }}
                  />
                  Auto-assign random partner if no one accepts
                </label>
              </div>
              <button
                type="submit"
                className="btn-primary"
                disabled={savingBatchTimeout}
              >
                {savingBatchTimeout ? 'Saving...' : 'Update Assignment Settings'}
              </button>
            </form>

            {batchTimeoutMessage.text && (
              <div className={`message ${batchTimeoutMessage.type}`}>
                {batchTimeoutMessage.text}
              </div>
            )}
          </div>

          {/* Tracking Map Settings */}
          <div className="settings-card">
            <h2>Live Tracking Map Settings</h2>

            <div className="settings-status">
              <div className="status-grid">
                <div className="status-item">
                  <div className="status-label">Current Marker Style</div>
                  <div className="status-value">
                    {deliveryMarkerStyle === 'scooter' ? '🛵 Scooter' : '🚗 Luxury Car'}
                  </div>
                </div>
              </div>
              <p className="status-description">
                This controls how the delivery partner appears on the live tracking map in the user app.
              </p>
            </div>

            <div className="settings-form">
              <h3>Delivery Partner Marker Style</h3>
              <p className="form-description">
                Choose the visual style for the delivery partner marker on the live order tracking map.
                This is a global setting that applies to all deliveries.
              </p>

              <div className="marker-style-options">
                <button
                  type="button"
                  className={`marker-option ${deliveryMarkerStyle === 'scooter' ? 'active' : ''}`}
                  onClick={() => handleUpdateTrackingMapSettings('scooter')}
                  disabled={savingTrackingMap || deliveryMarkerStyle === 'scooter'}
                >
                  <span className="marker-icon">🛵</span>
                  <span className="marker-label">Scooter</span>
                  <span className="marker-desc">Delivery boy on scooter with helmet</span>
                  {deliveryMarkerStyle === 'scooter' && <span className="marker-badge">Active</span>}
                </button>

                <button
                  type="button"
                  className={`marker-option ${deliveryMarkerStyle === 'car' ? 'active' : ''}`}
                  onClick={() => handleUpdateTrackingMapSettings('car')}
                  disabled={savingTrackingMap || deliveryMarkerStyle === 'car'}
                >
                  <span className="marker-icon">🚗</span>
                  <span className="marker-label">Luxury Car</span>
                  <span className="marker-desc">Premium BMW/Mercedes style car</span>
                  {deliveryMarkerStyle === 'car' && <span className="marker-badge">Active</span>}
                </button>
              </div>

              <h3 style={{ marginTop: '24px' }}>Marker Size</h3>
              <p className="form-description">
                Size of the user, restaurant, and delivery markers on the live tracking map
                (applies to all three). Current: <strong>{Math.round(markerScale * 100)}%</strong>.
              </p>

              <div className="marker-style-options">
                {[
                  { pct: 100, scale: 1.0 },
                  { pct: 90, scale: 0.9 },
                  { pct: 75, scale: 0.75 },
                  { pct: 60, scale: 0.6 },
                  { pct: 50, scale: 0.5 },
                ].map(({ pct, scale }) => {
                  const active = Math.round(markerScale * 100) === pct;
                  return (
                    <button
                      key={pct}
                      type="button"
                      className={`marker-option ${active ? 'active' : ''}`}
                      onClick={() => handleUpdateMarkerScale(scale)}
                      disabled={savingTrackingMap || active}
                    >
                      <span className="marker-label">{pct}%</span>
                      {active && <span className="marker-badge">Active</span>}
                    </button>
                  );
                })}
              </div>

              {savingTrackingMap && (
                <div className="saving-indicator">Updating marker settings...</div>
              )}

              {trackingMapMessage.text && (
                <div className={`message ${trackingMapMessage.type}`}>
                  {trackingMapMessage.text}
                </div>
              )}
            </div>
          </div>

          {/* Tax Rate Settings */}
          <div className="settings-card">
            <h2>Tax Rate Settings (GST & TDS)</h2>

            <div className="settings-status">
              <div className="status-grid">
                <div className="status-item">
                  <div className="status-label">Food GST</div>
                  <div className="status-value">{settings?.taxRates?.foodGst ?? 5}%</div>
                </div>
                <div className="status-item">
                  <div className="status-label">Platform GST</div>
                  <div className="status-value">{settings?.taxRates?.platformGst ?? 18}%</div>
                </div>
                <div className="status-item">
                  <div className="status-label">Delivery GST</div>
                  <div className="status-value">{settings?.taxRates?.deliveryGst ?? 18}%</div>
                </div>
                <div className="status-item">
                  <div className="status-label">TDS (194-O)</div>
                  <div className="status-value">{settings?.taxRates?.tdsRate194O ?? 0.1}%</div>
                </div>
              </div>
              <p className="status-description">
                These tax rates are used for calculating GST on orders and TDS on restaurant payouts.
              </p>
            </div>

            <div className="settings-form">
              <h3>Update Tax Rates</h3>
              <p className="form-description">
                Configure GST rates for food, platform fees, and delivery fees, as well as TDS rate under Section 194-O.
                Note: 18% GST applies to delivery fees (platform's GST liability).
                Changes will apply to new orders only. Existing orders retain their original tax rates.
              </p>

              <form onSubmit={handleUpdateTaxRates}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="foodGst">Food GST Rate (%)</label>
                    <input
                      type="number"
                      id="foodGst"
                      value={foodGst}
                      onChange={(e) => setFoodGst(e.target.value)}
                      min="0"
                      max="28"
                      step="0.5"
                      required
                      className="form-control"
                    />
                    <small className="form-help">
                      GST on restaurant food (typically 5% for non-AC)
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="platformGst">Platform Fee GST Rate (%)</label>
                    <input
                      type="number"
                      id="platformGst"
                      value={platformGst}
                      onChange={(e) => setPlatformGst(e.target.value)}
                      min="0"
                      max="28"
                      step="0.5"
                      required
                      className="form-control"
                    />
                    <small className="form-help">
                      GST on platform/service fees (SAC 998319)
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="deliveryGst">Delivery GST Rate (%)</label>
                    <input
                      type="number"
                      id="deliveryGst"
                      value={deliveryGst}
                      onChange={(e) => setDeliveryGst(e.target.value)}
                      min="0"
                      max="28"
                      step="0.5"
                      required
                      className="form-control"
                    />
                    <small className="form-help">
                      GST on delivery fees (platform's GST liability)
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="tdsRate">TDS Rate - Section 194-O (%)</label>
                    <input
                      type="number"
                      id="tdsRate"
                      value={tdsRate}
                      onChange={(e) => setTdsRate(e.target.value)}
                      min="0"
                      max="10"
                      step="0.1"
                      required
                      className="form-control"
                    />
                    <small className="form-help">
                      TDS deducted from restaurant payouts (e-commerce operators)
                    </small>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingTaxRates}
                >
                  {savingTaxRates ? 'Updating...' : 'Update Tax Rates'}
                </button>
              </form>

              {taxRatesMessage.text && (
                <div className={`message ${taxRatesMessage.type}`}>
                  {taxRatesMessage.text}
                </div>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className="info-box">
            <h3>ℹ️ Important Information</h3>
            <ul>
              <li>The free delivery partner limit determines how many delivery partners can register without paying the ₹500 onboarding fee.</li>
              <li>Once the limit is reached, new delivery partners will be required to pay the standard onboarding fee.</li>
              <li>Updating the limit will apply immediately to new registrations.</li>
              <li>You can manually adjust the "Slots Used" counter if corrections are needed (e.g., to add more free slots by reducing the used count).</li>
              <li>The slots used counter cannot exceed the total limit and must be a positive number.</li>
              <li><strong>Restaurant Distance:</strong> The maximum distance setting controls which restaurants are shown to users based on their location. A smaller distance shows fewer, closer restaurants.</li>
              <li><strong>Delivery Partner Distance:</strong> Controls how far delivery partners can be from the restaurant to receive order requests. Partners beyond this distance will be skipped during the 7+7+r assignment algorithm.</li>
              <li><strong>Minimum Required Partners:</strong> Sets the minimum number of available delivery partners needed near a restaurant for delivery to be shown as available. If fewer partners are available (within distance, not busy, no pending COD), delivery will be disabled for that restaurant.</li>
              <li><strong>Tracking Map:</strong> The delivery marker style controls how the delivery partner appears on the live tracking map. Choose between a scooter (delivery boy) or luxury car (BMW/Mercedes style) animation.</li>
              <li><strong>Tax Rate Settings:</strong> Configure GST rates (Food, Delivery, Platform) and TDS rate (Section 194-O). Changes apply only to new orders - existing orders retain their original tax rates.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
